import { useState } from "react";
import { X, Phone, Mail, MapPin, Calendar, PoundSterling, FileText, Flame, Snowflake, Thermometer, Archive, RotateCcw, Download, Printer, User, Send, Save, Edit2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LeadDetailModal = ({ lead, onClose, onUpdate, token }) => {
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [downloading, setDownloading] = useState({ vcard: false, pdf: false });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedLead, setEditedLead] = useState({
    customer_name: lead?.customer_name || lead?.name || "",
    email: lead?.email || "",
    phone: lead?.phone || "",
    postcode: lead?.postcode || "",
    project_type: lead?.project_type || "",
    estimate_low: lead?.estimate_low || lead?.estimated_min || 0,
    estimate_high: lead?.estimate_high || lead?.estimated_max || 0
  });
  const headers = { Authorization: `Bearer ${token}` };

  if (!lead) return null;

  const handleDownloadVCard = async () => {
    setDownloading(prev => ({ ...prev, vcard: true }));
    try {
      const response = await axios.get(`${API}/owner/leads/${lead.id}/vcard`, {
        headers,
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/vcard' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'contact.vcf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) filename = filenameMatch[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Contact downloaded");
    } catch (error) {
      console.error("Download vCard error:", error);
      toast.error("Failed to download contact");
    } finally {
      setDownloading(prev => ({ ...prev, vcard: false }));
    }
  };

  const handleDownloadEstimatePDF = async () => {
    setDownloading(prev => ({ ...prev, pdf: true }));
    try {
      const response = await axios.get(`${API}/owner/leads/${lead.id}/estimate-pdf`, {
        headers,
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'estimate.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) filename = filenameMatch[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Estimate PDF downloaded");
    } catch (error) {
      console.error("Download PDF error:", error);
      toast.error("Failed to download estimate PDF");
    } finally {
      setDownloading(prev => ({ ...prev, pdf: false }));
    }
  };

  // Save edited quote/lead data
  const handleSaveQuote = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/owner/leads/${lead.id}`, {
        customer_name: editedLead.customer_name,
        email: editedLead.email,
        phone: editedLead.phone,
        postcode: editedLead.postcode,
        project_type: editedLead.project_type,
        estimate_low: parseFloat(editedLead.estimate_low) || 0,
        estimate_high: parseFloat(editedLead.estimate_high) || 0
      }, { headers });
      toast.success("Quote details saved");
      setEditMode(false);
      onUpdate();
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save quote details");
    } finally {
      setSaving(false);
    }
  };

  // Send quote by email - one click with pre-populated email
  const handleSendQuoteEmail = async () => {
    const customerEmail = lead.email || editedLead.email;
    if (!customerEmail) {
      toast.error("No email address for this customer");
      return;
    }
    
    setSendingEmail(true);
    try {
      await axios.post(`${API}/owner/leads/${lead.id}/send-quote-email`, {
        to_email: customerEmail
      }, { headers });
      toast.success(`Quote sent to ${customerEmail}`);
    } catch (error) {
      console.error("Send email error:", error);
      // Show helpful message even if email not fully configured
      if (error.response?.status === 501 || error.response?.data?.detail?.includes("not configured")) {
        toast.info(`Email would be sent to: ${customerEmail}`, {
          description: "Email service not yet configured. Contact your admin."
        });
      } else {
        toast.error(error.response?.data?.detail || "Failed to send quote email");
      }
    } finally {
      setSendingEmail(false);
    }
  };

  const temperatureColors = {
    hot: { bg: "bg-red-100", text: "text-red-700", icon: Flame },
    warm: { bg: "bg-orange-100", text: "text-orange-700", icon: Thermometer },
    cold: { bg: "bg-blue-100", text: "text-blue-700", icon: Snowflake }
  };

  const statusLabels = {
    new_lead: { label: "New Lead", color: "bg-blue-100 text-blue-700" },
    new_enquiry: { label: "New Enquiry", color: "bg-blue-100 text-blue-700" },
    qualified: { label: "Qualified", color: "bg-purple-100 text-purple-700" },
    contacted: { label: "Contacted", color: "bg-yellow-100 text-yellow-700" },
    site_visit_scheduled: { label: "Site Visit", color: "bg-amber-100 text-amber-700" },
    quote_booked: { label: "Quote Booked", color: "bg-purple-100 text-purple-700" },
    quote_sent: { label: "Quote Sent", color: "bg-indigo-100 text-indigo-700" },
    job_won: { label: "Job Won", color: "bg-green-100 text-green-700" },
    job_lost: { label: "Job Lost", color: "bg-gray-100 text-gray-700" },
    job_completed: { label: "Completed", color: "bg-emerald-100 text-emerald-700" }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    try {
      await axios.post(`${API}/owner/leads/${lead.id}/notes`, { note: newNote }, { headers });
      toast.success("Note added");
      setNewNote("");
      onUpdate();
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/owner/leads/${lead.id}/status`, { status: newStatus }, { headers });
      toast.success("Status updated");
      onUpdate();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleTemperatureChange = async (temp) => {
    try {
      await axios.put(`${API}/owner/leads/${lead.id}/temperature?temperature=${temp}`, {}, { headers });
      toast.success(`Lead marked as ${temp}`);
      onUpdate();
    } catch (error) {
      toast.error("Failed to update temperature");
    }
  };

  const handleArchive = async () => {
    try {
      await axios.put(`${API}/owner/leads/${lead.id}/archive`, {}, { headers });
      toast.success("Lead archived");
      onUpdate();
      onClose();
    } catch (error) {
      toast.error("Failed to archive lead");
    }
  };

  const handleUnarchive = async () => {
    try {
      await axios.put(`${API}/owner/leads/${lead.id}/unarchive`, {}, { headers });
      toast.success("Lead unarchived");
      onUpdate();
      onClose();
    } catch (error) {
      toast.error("Failed to unarchive lead");
    }
  };

  const statusInfo = statusLabels[lead.status] || { label: lead.status, color: "bg-gray-100 text-gray-700" };
  const tempInfo = lead.temperature ? temperatureColors[lead.temperature] : null;
  const TempIcon = tempInfo?.icon;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 p-6 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold text-gray-900">
                {lead.customer_name || lead.name || "Unknown"}
              </h2>
              {tempInfo && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${tempInfo.bg} ${tempInfo.text}`}>
                  <TempIcon className="w-3 h-3" />
                  {lead.temperature}
                </span>
              )}
              {lead.archived && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                  Archived
                </span>
              )}
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
              {statusInfo.label}
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Contact Info */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <Phone className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Phone</p>
                <a href={`tel:${lead.phone}`} className="font-medium text-primary hover:underline">
                  {lead.phone || "Not provided"}
                </a>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <Mail className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <a href={`mailto:${lead.email}`} className="font-medium text-primary hover:underline">
                  {lead.email || "Not provided"}
                </a>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <MapPin className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Postcode</p>
                <p className="font-medium">{lead.postcode || "Not provided"}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <Calendar className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500">Date</p>
                <p className="font-medium">
                  {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "Unknown"}
                </p>
              </div>
            </div>
          </div>

          {/* Project Info - Editable Quote Section */}
          <div className="bg-primary/5 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">Quote Details</h3>
              {!editMode ? (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setEditMode(true)}
                  data-testid="edit-quote-btn"
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setEditMode(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    size="sm" 
                    onClick={handleSaveQuote}
                    disabled={saving}
                    data-testid="save-quote-btn"
                  >
                    <Save className="w-4 h-4 mr-1" />
                    {saving ? "Saving..." : "Save"}
                  </Button>
                </div>
              )}
            </div>
            
            {editMode ? (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Customer Name</Label>
                    <Input
                      value={editedLead.customer_name}
                      onChange={(e) => setEditedLead(prev => ({ ...prev, customer_name: e.target.value }))}
                      className="mt-1"
                      data-testid="edit-customer-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input
                      type="email"
                      value={editedLead.email}
                      onChange={(e) => setEditedLead(prev => ({ ...prev, email: e.target.value }))}
                      className="mt-1"
                      data-testid="edit-email"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input
                      value={editedLead.phone}
                      onChange={(e) => setEditedLead(prev => ({ ...prev, phone: e.target.value }))}
                      className="mt-1"
                      data-testid="edit-phone"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Postcode</Label>
                    <Input
                      value={editedLead.postcode}
                      onChange={(e) => setEditedLead(prev => ({ ...prev, postcode: e.target.value }))}
                      className="mt-1"
                      data-testid="edit-postcode"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Project Type</Label>
                  <Input
                    value={editedLead.project_type}
                    onChange={(e) => setEditedLead(prev => ({ ...prev, project_type: e.target.value }))}
                    className="mt-1"
                    data-testid="edit-project-type"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Estimate Low (£)</Label>
                    <Input
                      type="number"
                      value={editedLead.estimate_low}
                      onChange={(e) => setEditedLead(prev => ({ ...prev, estimate_low: e.target.value }))}
                      className="mt-1"
                      data-testid="edit-estimate-low"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Estimate High (£)</Label>
                    <Input
                      type="number"
                      value={editedLead.estimate_high}
                      onChange={(e) => setEditedLead(prev => ({ ...prev, estimate_high: e.target.value }))}
                      className="mt-1"
                      data-testid="edit-estimate-high"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Project Type</p>
                  <p className="font-medium">{lead.project_type || "General Enquiry"}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Estimate Range</p>
                  <p className="font-medium text-primary">
                    £{lead.estimate_low || lead.estimated_min || 0} - £{lead.estimate_high || lead.estimated_max || 0}
                  </p>
                </div>
              </div>
            )}
            
            {/* One-click Email Button */}
            <div className="mt-4 pt-4 border-t border-primary/20">
              <Button
                onClick={handleSendQuoteEmail}
                disabled={sendingEmail || !lead.email}
                className="w-full bg-green-600 hover:bg-green-700"
                data-testid="send-quote-email-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {sendingEmail ? "Sending..." : `Send Quote to ${lead.email || "No Email"}`}
              </Button>
              {!lead.email && (
                <p className="text-xs text-amber-600 mt-1 text-center">
                  Add an email address to send quote
                </p>
              )}
            </div>
          </div>

          {/* Temperature Flags */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Lead Temperature</h3>
            <div className="flex gap-2">
              <Button
                variant={lead.temperature === "cold" ? "default" : "outline"}
                size="sm"
                onClick={() => handleTemperatureChange("cold")}
                className={lead.temperature === "cold" ? "bg-blue-500 hover:bg-blue-600" : ""}
              >
                <Snowflake className="w-4 h-4 mr-1" />
                Cold
              </Button>
              <Button
                variant={lead.temperature === "warm" ? "default" : "outline"}
                size="sm"
                onClick={() => handleTemperatureChange("warm")}
                className={lead.temperature === "warm" ? "bg-orange-500 hover:bg-orange-600" : ""}
              >
                <Thermometer className="w-4 h-4 mr-1" />
                Warm
              </Button>
              <Button
                variant={lead.temperature === "hot" ? "default" : "outline"}
                size="sm"
                onClick={() => handleTemperatureChange("hot")}
                className={lead.temperature === "hot" ? "bg-red-500 hover:bg-red-600" : ""}
              >
                <Flame className="w-4 h-4 mr-1" />
                Hot
              </Button>
              {lead.temperature && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleTemperatureChange("none")}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Status Update - Pipeline Stages */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Pipeline Stage</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {["new_lead", "qualified", "site_visit_scheduled", "quote_sent", "job_won", "job_lost", "job_completed"].map((key) => {
                const val = statusLabels[key];
                const isActive = lead.status === key || lead.pipeline_stage === key;
                return (
                  <Button
                    key={key}
                    variant={isActive ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleStatusChange(key)}
                    className={isActive ? "" : "text-xs"}
                    data-testid={`status-btn-${key}`}
                  >
                    {val?.label || key}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Notes</h3>
            {Array.isArray(lead.notes) && lead.notes.length > 0 ? (
              <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
                {lead.notes.map((note, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm">{note.text || note}</p>
                    {note.added_at && (
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(note.added_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm mb-4">No notes yet</p>
            )}
            <div className="flex gap-2">
              <Input
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note..."
                onKeyPress={(e) => e.key === "Enter" && handleAddNote()}
              />
              <Button onClick={handleAddNote} disabled={addingNote}>
                {addingNote ? "Adding..." : "Add"}
              </Button>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-100 p-4">
          {/* Download Actions Row */}
          <div className="flex flex-wrap gap-2 mb-3 pb-3 border-b border-gray-200">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleDownloadVCard}
              disabled={downloading.vcard}
              className="text-blue-600 border-blue-200 hover:bg-blue-50"
              data-testid="download-vcard-btn"
            >
              <User className="w-4 h-4 mr-2" />
              {downloading.vcard ? "Downloading..." : "Download Contact"}
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleDownloadEstimatePDF}
              disabled={downloading.pdf}
              className="text-green-600 border-green-200 hover:bg-green-50"
              data-testid="download-estimate-pdf-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              {downloading.pdf ? "Generating..." : "Print Estimate"}
            </Button>
          </div>
          
          {/* Archive/Close Row */}
          <div className="flex justify-between">
            {lead.archived ? (
              <Button variant="outline" onClick={handleUnarchive} data-testid="restore-lead-btn">
                <RotateCcw className="w-4 h-4 mr-2" />
                Restore Lead
              </Button>
            ) : (
              <Button variant="outline" onClick={handleArchive} className="text-gray-500" data-testid="archive-lead-btn">
                <Archive className="w-4 h-4 mr-2" />
                Archive Lead
              </Button>
            )}
            <Button onClick={onClose} data-testid="close-lead-modal-btn">Close</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetailModal;
